/**
 * Centralized tile configuration for the feature tile API
 *
 * Controls the tile size for both Leaflet rendering and backend API requests.
 * Base map tile size is hardcoded to 256px in base-map-provider.ts.
 *
 * ============================================================================
 * HOW FEATURE TILE CONFIGURATION WORKS
 * ============================================================================
 *
 * CRITICAL: tileSizePx and tileSizeParam MUST be synchronized!
 *   - tileSizePx: Controls Leaflet's tile grid (determines z/x/y coordinates)
 *   - tileSizeParam: Tells backend which grid to use for clustering
 *
 * If these don't match, Leaflet requests wrong coordinates and no features load.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │                    FEATURE TILE API REQUEST FLOW                        │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * 1. LEAFLET CALCULATES (using tileSizePx):
 *    ┌──────────────────────────────────────────────────────────────────┐
 *    │ Current Zoom: 12, tileSize: 512px                                │
 *    │ → Calculate tile coordinates: {z}=12, {x}=706, {y}=1640         │
 *    └──────────────────────────────────────────────────────────────────┘
 *
 * 2. LEAFLET BUILDS URL (using tileSizeParam):
 *    ┌──────────────────────────────────────────────────────────────────┐
 *    │ Template: /tiles/{z}/{x}/{y}?tileSize=Large512&filter=...       │
 *    │ Result: /tiles/12/706/1640?tileSize=Large512&filter=...         │
 *    └──────────────────────────────────────────────────────────────────┘
 *
 * 3. BACKEND PROCESSES:
 *    ┌──────────────────────────────────────────────────────────────────┐
 *    │ Read: tileSize=Large512 → Use 512px grid at (12, 706, 1640)     │
 *    │ Returns: GeoJSON with features/clusters in this tile            │
 *    └──────────────────────────────────────────────────────────────────┘
 *
 * TILE SIZE OPTIONS:
 *   - 256: More requests, smaller payloads, finer clustering (tileSizeParam: 'Standard256')
 *   - 512: Fewer requests, larger payloads, coarser clustering (tileSizeParam: 'Large512')
 *
 * NOTE: Base map tiles (imagery) are always 256px, configured in base-map-provider.ts
 */

export interface TileConfig {
    /** Tile size in pixels for Leaflet rendering (must match tileSizeParam) */
    tileSizePx: number;
    /** Tile size parameter for backend API (must match tileSizePx) */
    tileSizeParam: string;
}

// ============================================================================
// MAIN CONFIGURATION - Change BOTH values together to switch tile sizes
// ============================================================================

/**
 * Active tile configuration - import this in your services
 *
 * Options (always change BOTH together):
 *   - { tileSizePx: 256, tileSizeParam: 'Standard256' } - Recommended
 *   - { tileSizePx: 512, tileSizeParam: 'Large512' } - Fewer requests
 */
export const TILE_CONFIG: TileConfig = {
    tileSizePx: 512,
    tileSizeParam: 'Large512'
};
